import { useEffect } from 'react'
import { useThree } from '@react-three/fiber'
import { Vector3 } from 'three'
import type { RefObject } from 'react'
import type { ThreeEvent } from '@react-three/fiber'
import type { TrackballControls as TrackballControlsImpl } from 'three-stdlib'

import { useCADQuery } from '@/services/queries/cads'

import { useAppStore } from '@/state'
import { useCADPageStore } from '@/state/cad'
import { clickCubeFace as clickCubeFaceTool } from '@/lib/tools/clickCubeFace'
import type { CubeFace } from '@/lib/tools/clickCubeFace'

const waitForCameraIdle = () => {
  return new Promise<void>((resolve, reject) => {
    const startedAt = Date.now()

    const check = () => {
      if (!useCADPageStore.getState().fitting) {
        resolve()
        return
      }

      if (Date.now() - startedAt > 5000) {
        reject(new Error('Timed out waiting for CAD camera to finish fitting'))
        return
      }

      requestAnimationFrame(check)
    }

    requestAnimationFrame(check)
  })
}

export const useThreeWindowInit = ({
  setRotationTo,
}: {
  setRotationTo: (e: ThreeEvent<MouseEvent>, direction: Vector3) => null
  trackballControlsRef: RefObject<TrackballControlsImpl | null>
}) => {
  const { camera, gl } = useThree()

  const { data: cadData } = useCADQuery()
  const gltf = cadData?.gltf

  useEffect(() => {
    if (window.Cypress || window.lambdaAutomation) {
      // @ts-expect-error - extending window for Cypress testing & automation
      window.$$appStore = useAppStore

      // @ts-expect-error - extending window for Cypress testing & automation
      window.$$CADPageStore = useCADPageStore

      // @ts-expect-error - Expose the GLTF object to the window for testing and automation
      window.$$Q20 = {
        gltf,
        camera,
        domElement: gl.domElement,
        clickCubeFace: (face: CubeFace) => {
          return clickCubeFaceTool({
            face,
            setRotationTo,
            waitForCameraIdle,
          })
        },
      }

      if (gltf) {
        // @ts-expect-error - set gltf loaded
        window.$$GLTF_loaded = true
      }
    }
  }, [camera, gl.domElement, gltf, setRotationTo])
}
